/**
 * Web Audio Synthesizer for Space Invaders - Pink Pixel OS
 * Pure procedural synthesis for authentic 8-bit arcade sounds.
 * Automatically cleans up audio context on teardown.
 */

class SpaceInvadersAudio {
  constructor() {
    this.ctx = null
    this.isMuted = false
    this.marchPitches = [175, 160, 145, 130]
    this.marchIndex = 0
    this.ufoOsc = null
    this.ufoGain = null
  }

  init() {
    if (!this.ctx) {
      const AudioCtx = window.AudioContext || window.webkitAudioContext
      if (AudioCtx) {
        this.ctx = new AudioCtx()
      }
    }
    if (this.ctx && this.ctx.state === 'suspended') {
      this.ctx.resume()
    }
  }

  setMuted(muted) {
    this.isMuted = muted
    if (muted && this.ufoGain) {
      this.ufoGain.gain.setValueAtTime(0, this.ctx.currentTime)
    }
  }

  playShoot() {
    if (this.isMuted) return
    this.init()
    if (!this.ctx) return

    const now = this.ctx.currentTime
    const osc = this.ctx.createOscillator()
    const gain = this.ctx.createGain()

    osc.type = 'sawtooth'
    osc.frequency.setValueAtTime(880, now)
    osc.frequency.exponentialRampToValueAtTime(110, now + 0.12)

    gain.gain.setValueAtTime(0.2, now)
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.12)

    osc.connect(gain)
    gain.connect(this.ctx.destination)

    osc.start(now)
    osc.stop(now + 0.12)
  }

  playMarchStep() {
    if (this.isMuted) return
    this.init()
    if (!this.ctx) return

    const now = this.ctx.currentTime
    const osc = this.ctx.createOscillator()
    const gain = this.ctx.createGain()

    const pitch = this.marchPitches[this.marchIndex % this.marchPitches.length]
    this.marchIndex++

    osc.type = 'triangle'
    osc.frequency.setValueAtTime(pitch, now)

    gain.gain.setValueAtTime(0.3, now)
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.08)

    osc.connect(gain)
    gain.connect(this.ctx.destination)

    osc.start(now)
    osc.stop(now + 0.08)
  }

  playInvaderKilled() {
    if (this.isMuted) return
    this.init()
    if (!this.ctx) return

    const now = this.ctx.currentTime
    const osc = this.ctx.createOscillator()
    const gain = this.ctx.createGain()

    osc.type = 'square'
    osc.frequency.setValueAtTime(220, now)
    osc.frequency.setValueAtTime(110, now + 0.04)

    gain.gain.setValueAtTime(0.25, now)
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.1)

    osc.connect(gain)
    gain.connect(this.ctx.destination)

    osc.start(now)
    osc.stop(now + 0.1)
  }

  playPlayerExplosion() {
    if (this.isMuted) return
    this.init()
    if (!this.ctx) return

    const now = this.ctx.currentTime
    const bufferSize = this.ctx.sampleRate * 0.4
    const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate)
    const data = buffer.getChannelData(0)
    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1
    }

    const noise = this.ctx.createBufferSource()
    noise.buffer = buffer

    const filter = this.ctx.createBiquadFilter()
    filter.type = 'lowpass'
    filter.frequency.setValueAtTime(800, now)
    filter.frequency.linearRampToValueAtTime(80, now + 0.4)

    const gain = this.ctx.createGain()
    gain.gain.setValueAtTime(0.4, now)
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.4)

    noise.connect(filter)
    filter.connect(gain)
    gain.connect(this.ctx.destination)

    noise.start(now)
  }

  startUfoSound() {
    if (this.isMuted || this.ufoOsc) return
    this.init()
    if (!this.ctx) return

    const now = this.ctx.currentTime
    this.ufoOsc = this.ctx.createOscillator()
    this.ufoGain = this.ctx.createGain()

    this.ufoOsc.type = 'sawtooth'
    this.ufoOsc.frequency.setValueAtTime(450, now)

    // Warble LFO
    const lfo = this.ctx.createOscillator()
    const lfoGain = this.ctx.createGain()
    lfo.frequency.setValueAtTime(6, now)
    lfoGain.gain.setValueAtTime(80, now)
    lfo.connect(this.ufoOsc.frequency)
    lfo.start(now)
    this.ufoLfo = lfo

    this.ufoGain.gain.setValueAtTime(0.12, now)
    this.ufoOsc.connect(this.ufoGain)
    this.ufoGain.connect(this.ctx.destination)
    this.ufoOsc.start(now)
  }

  stopUfoSound() {
    if (this.ufoOsc) {
      try {
        this.ufoOsc.stop()
        this.ufoLfo?.stop()
        this.ufoOsc.disconnect()
      } catch {
        // ignore if already stopped
      }
      this.ufoOsc = null
      this.ufoLfo = null
      this.ufoGain = null
    }
  }

  destroy() {
    this.stopUfoSound()
    if (this.ctx) {
      try {
        this.ctx.close()
      } catch {
        // ignore
      }
      this.ctx = null
    }
  }
}

export function createAudio() {
  return new SpaceInvadersAudio()
}
